import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';

export type PlanningJourneyPhase = {
  rid?: number;
  rix?: string;
  shc?: string;
  shortcode?: string;
  nam?: string;
  recname?: string;
  sor?: number;
  sortorder?: number;
  ast?: number;
  status?: number;
};

type JourneyState =
  | 'completed'
  | 'current'
  | 'workspace'
  | 'planning'
  | 'upcoming'
  | 'cancelled';

type Props = {
  phases: PlanningJourneyPhase[];

  /**
   * Phase visually selected in the Journey.
   * Outside the planning window this normally equals governancePhaseCode.
   * Inside the planning window it normally equals workspacePhaseCode.
   */
  currentPhaseCode?: string | null;

  /**
   * DB-authoritative Planning Cycle phase.
   */
  governancePhaseCode?: string | null;

  /**
   * Identity of the currently rendered workbench.
   */
  workspacePhaseCode?: string | null;

  /**
   * Inclusive planning workspace boundaries.
   */
  planningStartPhaseCode?: string;
  planningEndPhaseCode?: string;

  compact?: boolean;
  showCaption?: boolean;
  caption?: string;

  completedColor?: string;
  currentColor?: string;
  planningColor?: string;
  upcomingColor?: string;
  cancelledColor?: string;

  cancelled?: boolean;
  sx?: any;
};

class FPlanningJourney extends React.PureComponent<Props> {
  static defaultProps: Partial<Props> = {
    planningStartPhaseCode: 'BUDGET_CONTROL',
    planningEndPhaseCode: 'GOVERNANCE',

    compact: true,
    showCaption: false,
    caption: 'Planning Journey',

    completedColor: '#9e9e9e',
    currentColor: '#1976d2',
    planningColor: '#bdbdbd',
    upcomingColor: '#e0e0e0',
    cancelledColor: '#d32f2f',

    cancelled: false,
  };

  private normalizeCode = (value?: string | null): string =>
    String(value || '').trim().toUpperCase();

  private codeOf = (p: PlanningJourneyPhase): string =>
    this.normalizeCode(p.shc ?? p.shortcode);

  private nameOf = (p: PlanningJourneyPhase): string =>
    String(p.nam ?? p.recname ?? this.codeOf(p));

  private orderOf = (p: PlanningJourneyPhase): number =>
    Number(p.sor ?? p.sortorder ?? 0);

  private phaseIndex = (
    phases: PlanningJourneyPhase[],
    code?: string | null,
  ): number => {
    const normalized = this.normalizeCode(code);

    if (!normalized) return -1;

    return phases.findIndex(
      (phase) => this.codeOf(phase) === normalized,
    );
  };

  private isPlanningGovernanceWindow = (
    phases: PlanningJourneyPhase[],
  ): boolean => {
    const governanceIndex = this.phaseIndex(
      phases,
      this.props.governancePhaseCode,
    );

    const planningStartIndex = this.phaseIndex(
      phases,
      this.props.planningStartPhaseCode,
    );

    const planningEndIndex = this.phaseIndex(
      phases,
      this.props.planningEndPhaseCode,
    );

    if (
      governanceIndex < 0 ||
      planningStartIndex < 0 ||
      planningEndIndex < 0
    ) {
      return false;
    }

    /*
     * Under the new governance model the authoritative Planning Cycle
     * remains at BUDGET_CONTROL while the user works anywhere inside
     * the planning subsystem.
     *
     * Do not infer planning mode merely because the displayed workspace
     * happens to fall between the boundary nodes.
     */
    return governanceIndex === planningStartIndex;
  };

  private stateOf = (
    phases: PlanningJourneyPhase[],
    index: number,
  ): JourneyState => {
    if (this.props.cancelled) return 'cancelled';

    const governanceIndex = this.phaseIndex(
      phases,
      this.props.governancePhaseCode,
    );

    const displayIndex = this.phaseIndex(
      phases,
      this.props.currentPhaseCode,
    );

    const workspaceIndex = this.phaseIndex(
      phases,
      this.props.workspacePhaseCode,
    );

    const planningStartIndex = this.phaseIndex(
      phases,
      this.props.planningStartPhaseCode,
    );

    const planningEndIndex = this.phaseIndex(
      phases,
      this.props.planningEndPhaseCode,
    );

    const planningMode = this.isPlanningGovernanceWindow(phases);

    /*
     * PLANNING MODE
     *
     * Everything before BUDGET_CONTROL is genuinely completed.
     *
     * Everything from BUDGET_CONTROL through GOVERNANCE belongs to the
     * active planning workspace. Only the workbench being viewed is
     * highlighted; the other planning nodes are neutral.
     *
     * EXECUTION and beyond remain upcoming.
     */
    if (
      planningMode &&
      planningStartIndex >= 0 &&
      planningEndIndex >= planningStartIndex
    ) {
      if (index < planningStartIndex) {
        return 'completed';
      }

      if (
        index >= planningStartIndex &&
        index <= planningEndIndex
      ) {
        if (index === workspaceIndex || index === displayIndex) {
          return 'workspace';
        }

        return 'planning';
      }

      return 'upcoming';
    }

    /*
     * NORMAL GOVERNANCE MODE
     *
     * Outside the planning workspace, authoritative DB phase controls
     * the normal completed/current/upcoming lifecycle rendering.
     */
    const authoritativeIndex =
      governanceIndex >= 0 ? governanceIndex : displayIndex;

    if (authoritativeIndex < 0) {
      return 'upcoming';
    }

    if (index < authoritativeIndex) {
      return 'completed';
    }

    if (index === authoritativeIndex) {
      return 'current';
    }

    return 'upcoming';
  };

  private colorOf = (state: JourneyState): string | undefined => {
    switch (state) {
      case 'completed':
        return this.props.completedColor;

      case 'current':
      case 'workspace':
        return this.props.currentColor;

      case 'planning':
        return this.props.planningColor;

      case 'cancelled':
        return this.props.cancelledColor;

      default:
        return this.props.upcomingColor;
    }
  };

  private labelOf = (state: JourneyState): string => {
    switch (state) {
      case 'completed':
        return 'Completed';

      case 'current':
        return 'Current phase';

      case 'workspace':
        return 'Current workspace';

      case 'planning':
        return 'Planning workspace';

      case 'cancelled':
        return 'Cycle cancelled';

      default:
        return 'Upcoming';
    }
  };

  private connectorColor = (
    phases: PlanningJourneyPhase[],
    index: number,
  ): string | undefined => {
    if (index <= 0) {
      return this.props.upcomingColor;
    }

    const leftState = this.stateOf(phases, index - 1);
    const rightState = this.stateOf(phases, index);

    /*
     * Completed lifecycle connector.
     */
    if (
      leftState === 'completed' &&
      (
        rightState === 'completed' ||
        rightState === 'current' ||
        rightState === 'workspace' ||
        rightState === 'planning'
      )
    ) {
      return this.props.completedColor;
    }

    /*
     * Inside the planning subsystem the connector is neutral.
     * It must not imply sequential completion.
     */
    if (
      ['workspace', 'planning'].includes(leftState) &&
      ['workspace', 'planning'].includes(rightState)
    ) {
      return this.props.planningColor;
    }

    return this.props.upcomingColor;
  };

  render() {
    const phases = [...(this.props.phases || [])]
      .filter(
        (p) =>
          Number(p.ast ?? p.status ?? 1) === 1,
      )
      .sort(
        (a, b) =>
          this.orderOf(a) - this.orderOf(b),
      );

    if (!phases.length) return null;

    const dotSize = this.props.compact ? 10 : 12;
    const activeSize = this.props.compact ? 13 : 16;

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 0.5,
          ...this.props.sx,
        }}
      >
        {this.props.showCaption && (
          <Typography
            variant="caption"
            color="text.secondary"
          >
            {this.props.caption}
          </Typography>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexWrap: 'nowrap',
          }}
        >
          {phases.map((phase, index) => {
            const state = this.stateOf(
              phases,
              index,
            );

            const color = this.colorOf(state);
            const label = this.labelOf(state);

            const active =
              state === 'current' ||
              state === 'workspace';

            return (
              <React.Fragment
                key={
                  phase.rix ||
                  phase.rid ||
                  this.codeOf(phase)
                }
              >
                {index > 0 && (
                  <Box
                    sx={{
                      width: this.props.compact
                        ? 10
                        : 14,
                      height: 2,
                      bgcolor:
                        this.connectorColor(
                          phases,
                          index,
                        ),
                      flexShrink: 0,
                    }}
                  />
                )}

                <Tooltip
                  title={`${this.nameOf(
                    phase,
                  )} — ${label}`}
                  arrow
                >
                  <Box
                    component="span"
                    sx={{
                      width: active
                        ? activeSize
                        : dotSize,
                      height: active
                        ? activeSize
                        : dotSize,
                      borderRadius: '50%',
                      bgcolor: color,

                      border: active
                        ? '2px solid rgba(25,118,210,0.20)'
                        : state === 'planning'
                          ? '1px solid rgba(0,0,0,0.14)'
                          : '1px solid rgba(0,0,0,0.08)',

                      boxShadow: active
                        ? '0 0 0 3px rgba(25,118,210,0.10)'
                        : 'none',

                      transition:
                        'all 160ms ease',

                      cursor: 'default',
                      flexShrink: 0,
                    }}
                  />
                </Tooltip>
              </React.Fragment>
            );
          })}
        </Box>
      </Box>
    );
  }
}

export default FPlanningJourney;